import { GameState } from '../domain/types.ts';
import { GameEngine } from '../simulation/gameEngine.ts';
import { Camera } from '../render/camera.ts';
import { CanvasRenderer } from '../render/canvasRenderer.ts';
import { PointerController } from '../input/pointerController.ts';
import { SearchService, LocationResult } from '../map/searchService.ts';
import { AiMapGenerator, GeneratedConditions } from '../services/aiMapGenerator.ts';
import { RealMapLoader, RealMapData } from '../map/realMapLoader.ts';
import { GameTheoryHandbook } from './handbook.ts';
import { Haptics } from '../utils/haptics.ts';
import { LocationService } from '../map/locationService.ts';

const DEFAULT_MAP_LOCATIONS: Record<
  import('../domain/types.ts').MapId,
  { coords: { lat: number; lon: number }; label: string; region: 'north' | 'central' | 'south' }
> = {
  'cau-giay': {
    coords: { lat: 21.0368, lon: 105.7905 },
    label: 'Ngõ 165 Cầu Giấy, Cầu Giấy, Hà Nội',
    region: 'north',
  },
  'ta-hien': {
    coords: { lat: 21.0338, lon: 105.8504 },
    label: 'Phố Cổ Tạ Hiện, Hoàn Kiếm, Hà Nội',
    region: 'north',
  },
  'linh-dam': {
    coords: { lat: 20.9722, lon: 105.8291 },
    label: 'Bán Đảo Linh Đàm, Hoàng Mai, Hà Nội',
    region: 'north',
  },
};

const findLocationByQuery = (q: string): LocationResult => {
  const lower = q.toLowerCase();
  const matched = SearchService.FALLBACK_LOCATIONS.find(
    (loc) =>
      loc.label.toLowerCase().includes(lower) ||
      lower.includes(loc.label.toLowerCase().split(',')[0].toLowerCase())
  );
  return matched || SearchService.FALLBACK_LOCATIONS[0]!;
};

export class HudManager {
  private engine: GameEngine;
  private camera: Camera;
  private renderer: CanvasRenderer;
  private pointerController?: PointerController;

  // DOM Elements
  private bowelsBar: HTMLElement;
  private bagsValue: HTMLElement;
  private repValue: HTMLElement;
  private stressBar: HTMLElement;
  private paranoiaBar: HTMLElement;
  private toastBox: HTMLElement;

  private dilemmaOverlay: HTMLElement;
  private countdownBadge: HTMLElement;
  private btnCooperate: HTMLElement;
  private btnDefect: HTMLElement;
  private btnBlame: HTMLElement;

  private qteOverlay: HTMLElement;
  private qteSweetSpot: HTMLElement;
  private qteCursor: HTMLElement;
  private btnQTEKick: HTMLElement;

  private viralModal: HTMLElement;
  private viralReason: HTMLElement;
  private viralSnapshot: HTMLImageElement;
  private viralDisgraceBadge: HTMLElement;
  private btnPlayAgain: HTMLElement;
  private btnShareThreads: HTMLElement;

  private splashScreen: HTMLElement;
  private lobbyModal: HTMLElement;
  private inpPlayerName: HTMLInputElement;
  private btnEnterGame: HTMLElement;
  private btnOpenLobby: HTMLElement;

  // Popdown Menu Elements (Chuẩn BaChuViaHe)
  private btnOpenMenu: HTMLElement;
  private popdownBackdrop: HTMLElement;
  private popdownMenu: HTMLElement;
  private btnClosePopdown: HTMLElement;
  private popItemSearch: HTMLElement;
  private popItemLobby: HTMLElement;
  private popItemProfile: HTMLElement;
  private popItemHandbook: HTMLElement;
  private popItemHaptics: HTMLElement;
  private popItemRestart: HTMLElement;
  private hapticsStatusText: HTMLElement;
  private isHapticsEnabled = true;

  // Search & AI Map Modal Elements
  private searchModal: HTMLElement;
  private btnCloseSearch: HTMLElement;
  private inpSearchQuery: HTMLInputElement;
  private btnExecuteSearch: HTMLElement;
  private searchResultsList: HTMLElement;
  private aiGenPreview: HTMLElement;
  private aiGenTitle: HTMLElement;
  private aiGenDesc: HTMLElement;
  private btnConfirmAiMap: HTMLElement;
  private currentPendingAiConditions: GeneratedConditions | null = null;
  private currentPendingRealMap: RealMapData | null = null;

  // Handbook Modal
  private handbookModal: HTMLElement;

  private gameMasterBar: HTMLElement;
  private gmTopicBadge: HTMLElement;
  private gmText: HTMLElement;

  private selectedBreed: import('../domain/types.ts').DogBreed = 'corgi';
  private selectedMap: import('../domain/types.ts').MapId = 'cau-giay';

  private btnZoomIn: HTMLElement;
  private btnZoomOut: HTMLElement;
  private btnCenterPlayer: HTMLElement;

  private btnPlayCurrentLocation: HTMLElement | null = null;
  private popItemCurrentLocation: HTMLElement | null = null;
  private btnSearchMyGps: HTMLElement | null = null;
  private popItemPromo: HTMLElement | null = null;
  private promoOutroModal: HTMLElement | null = null;
  private btnClosePromoOutro: HTMLElement | null = null;

  constructor(
    engine: GameEngine,
    camera: Camera,
    renderer: CanvasRenderer,
    pointerController?: PointerController
  ) {
    this.engine = engine;
    this.camera = camera;
    this.renderer = renderer;
    this.pointerController = pointerController;

    // Grab elements
    this.bowelsBar = document.getElementById('bowelsBar')!;
    this.bagsValue = document.getElementById('bagsValue')!;
    this.repValue = document.getElementById('repValue')!;
    this.stressBar = document.getElementById('stressBar')!;
    this.paranoiaBar = document.getElementById('paranoiaBar')!;
    this.toastBox = document.getElementById('toastBox')!;

    this.dilemmaOverlay = document.getElementById('dilemmaOverlay')!;
    this.countdownBadge = document.getElementById('countdownBadge')!;
    this.btnCooperate = document.getElementById('btnCooperate')!;
    this.btnDefect = document.getElementById('btnDefect')!;
    this.btnBlame = document.getElementById('btnBlame')!;

    this.qteOverlay = document.getElementById('qteOverlay')!;
    this.qteSweetSpot = document.getElementById('qteSweetSpot')!;
    this.qteCursor = document.getElementById('qteCursor')!;
    this.btnQTEKick = document.getElementById('btnQTEKick')!;

    this.viralModal = document.getElementById('viralModal')!;
    this.viralReason = document.getElementById('viralReason')!;
    this.viralSnapshot = document.getElementById('viralSnapshot') as HTMLImageElement;
    this.viralDisgraceBadge = document.getElementById('viralDisgraceBadge')!;
    this.btnPlayAgain = document.getElementById('btnPlayAgain')!;
    this.btnShareThreads = document.getElementById('btnShareThreads')!;

    // Splash & Lobby Screen (BaChuViaHe style)
    this.splashScreen = document.getElementById('splash-screen')!;
    this.lobbyModal = document.getElementById('lobby-modal')!;
    this.inpPlayerName = document.getElementById('inpPlayerName') as HTMLInputElement;
    this.btnEnterGame = document.getElementById('btnEnterGame')!;
    this.btnOpenLobby = document.getElementById('btnOpenLobby')!;

    // Popdown Menu Elements
    this.btnOpenMenu = document.getElementById('btnOpenMenu')!;
    this.popdownBackdrop = document.getElementById('popdownBackdrop')!;
    this.popdownMenu = document.getElementById('popdownMenu')!;
    this.btnClosePopdown = document.getElementById('btnClosePopdown')!;
    this.popItemSearch = document.getElementById('popItemSearch')!;
    this.popItemLobby = document.getElementById('popItemLobby')!;
    this.popItemProfile = document.getElementById('popItemProfile')!;
    this.popItemHandbook = document.getElementById('popItemHandbook')!;
    this.popItemHaptics = document.getElementById('popItemHaptics')!;
    this.popItemRestart = document.getElementById('popItemRestart')!;
    this.hapticsStatusText = document.getElementById('hapticsStatusText')!;

    // Search & AI Map Elements
    this.searchModal = document.getElementById('searchModal')!;
    this.btnCloseSearch = document.getElementById('btnCloseSearch')!;
    this.inpSearchQuery = document.getElementById('inpSearchQuery') as HTMLInputElement;
    this.btnExecuteSearch = document.getElementById('btnExecuteSearch')!;
    this.searchResultsList = document.getElementById('searchResultsList')!;
    this.aiGenPreview = document.getElementById('aiGenPreview')!;
    this.aiGenTitle = document.getElementById('aiGenTitle')!;
    this.aiGenDesc = document.getElementById('aiGenDesc')!;
    this.btnConfirmAiMap = document.getElementById('btnConfirmAiMap')!;

    // Handbook
    this.handbookModal = document.getElementById('handbookModal')!;

    // GameMaster Live Bar
    this.gameMasterBar = document.getElementById('gameMasterBar')!;
    this.gmTopicBadge = document.getElementById('gmTopicBadge')!;
    this.gmText = document.getElementById('gmText')!;

    this.btnZoomIn = document.getElementById('btnZoomIn')!;
    this.btnZoomOut = document.getElementById('btnZoomOut')!;
    this.btnCenterPlayer = document.getElementById('btnCenterPlayer')!;

    this.btnPlayCurrentLocation = document.getElementById('btnPlayCurrentLocation');
    this.popItemCurrentLocation = document.getElementById('popItemCurrentLocation');
    this.btnSearchMyGps = document.getElementById('btnSearchMyGps');
    this.popItemPromo = document.getElementById('popItemPromo');
    this.promoOutroModal = document.getElementById('promoOutroModal');
    this.btnClosePromoOutro = document.getElementById('btnClosePromoOutro');

    this.setupEvents();
  }

  private setupEvents(): void {
    // 1. Splash Screen Tap to Join -> Show Lobby
    if (this.splashScreen) {
      this.splashScreen.addEventListener('click', () => {
        this.splashScreen.style.opacity = '0';
        setTimeout(() => {
          this.splashScreen.style.display = 'none';
          if (this.lobbyModal) {
            this.lobbyModal.style.display = 'flex';
            this.engine.setPhase('LOBBY');
          }
        }, 300);
      });
    }

    // 2. Open Lobby from Floating HUD Button
    if (this.btnOpenLobby) {
      this.btnOpenLobby.addEventListener('click', () => {
        if (this.lobbyModal) {
          this.lobbyModal.style.display = 'flex';
          this.engine.setPhase('LOBBY');
        }
      });
    }

    // 3. Breed Selection Cards
    const breedCards = document.querySelectorAll<HTMLElement>('.breed-card');
    breedCards.forEach((card) => {
      card.addEventListener('click', () => {
        breedCards.forEach((c) => c.classList.remove('selected'));
        card.classList.add('selected');
        const b = card.dataset.breed as import('../domain/types.ts').DogBreed;
        if (b) {
          this.selectedBreed = b;
        }
      });
    });

    // Lobby Search & Recent Map Elements
    const inpLobbySearch = document.getElementById('inpLobbySearch') as HTMLInputElement;
    const btnLobbySearch = document.getElementById('btnLobbySearch');
    const lobbySearchResultsList = document.getElementById('lobbySearchResultsList');
    const lobbyRecentBadge = document.getElementById('lobbyRecentBadge');
    const lobbyRecentCardContainer = document.getElementById('lobbyRecentCardContainer');

    const setEnterGameLoading = (isLoading: boolean, customText?: string) => {
      if (!this.btnEnterGame) return;
      const btn = this.btnEnterGame as HTMLButtonElement;
      if (isLoading) {
        btn.disabled = true;
        btn.style.opacity = '0.65';
        btn.style.cursor = 'wait';
        btn.textContent = customText || '⏳ ĐANG NẠP BẢN ĐỒ OSM...';
      } else {
        btn.disabled = false;
        btn.style.opacity = '1.0';
        btn.style.cursor = 'pointer';
        btn.textContent = customText || '▶ BƯỚC VÀO NGÕ (SẴN SÀNG)';
      }
    };

    const loadMapWithFeedback = async (
      coords: { lat: number; lon: number },
      label: string,
      region?: 'north' | 'central' | 'south'
    ): Promise<boolean> => {
      const r = region || SearchService.detectRegion(label);
      const shortName = label.split(',')[0] || label;
      setEnterGameLoading(true, `⏳ Đang nạp bản đồ OSM (${shortName})...`);

      try {
        const cond = await AiMapGenerator.generateInitialConditions(label, r);
        this.currentPendingAiConditions = cond;

        const loadPromise = RealMapLoader.loadRealMap(coords, label, cond);
        const timeoutPromise = new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('OSM_TIMEOUT')), 10000)
        );

        const realMap = (await Promise.race([loadPromise, timeoutPromise])) as RealMapData | null;
        if (realMap && realMap.isRealOsm) {
          this.currentPendingRealMap = realMap;
          setEnterGameLoading(false, `▶ BƯỚC VÀO NGÕ (${shortName} - SẴN SÀNG)`);
          this.engine.showToast(`🗺️ Đã tải bản đồ thực tế ${realMap.roads.length} tuyến đường OSM!`, 'success');
          return true;
        } else {
          throw new Error('OSM_FAILED');
        }
      } catch (err) {
        console.warn('[HudManager] Lỗi tải OSM map, gợi ý về map mặc định:', err);
        this.engine.showToast(
          '⚠️ Mạng OpenStreetMap đang bận! Đang gợi ý chuyển sang bản đồ mặc định...',
          'warning'
        );
        this.selectedMap = 'cau-giay';
        this.currentPendingRealMap = null;
        this.currentPendingAiConditions = null;

        document.querySelectorAll('.map-card').forEach((c) => c.classList.remove('selected'));
        document.querySelector('.map-card[data-map="cau-giay"]')?.classList.add('selected');
        setEnterGameLoading(false, '▶ BƯỚC VÀO NGÕ (MẶC ĐỊNH CẦU GIẤY)');
        return false;
      }
    };

    // Nút Chơi tại Vị trí của bạn (Lobby)
    this.btnPlayCurrentLocation?.addEventListener('click', async () => {
      this.engine.showToast('📍 Đang định vị GPS / IP của bạn...', 'info');
      setEnterGameLoading(true, '📍 Đang định vị GPS / IP...');

      try {
        const loc = await LocationService.getCurrentLocation();
        this.engine.showToast(`🎯 Đã tìm thấy: ${loc.streetName}! Đang tải bản đồ...`, 'info');

        const ok = await loadMapWithFeedback(loc.coords, loc.label, loc.region);
        if (ok) {
          const targetMapId = loc.region === 'north' ? 'cau-giay' : loc.region === 'south' ? 'ta-hien' : 'linh-dam';
          this.selectedMap = targetMapId;
          SearchService.saveLastPlayedMap({
            mapId: targetMapId,
            label: loc.label,
            region: loc.region,
            paranoia: 20,
            aiConditions: this.currentPendingAiConditions || undefined,
          });
          restoreRecentMap();
          document.querySelectorAll('.map-card').forEach((c) => c.classList.remove('selected'));
          document.getElementById('cardRecentMap')?.classList.add('selected');
        }
      } catch (err) {
        console.error('[HudManager] Lỗi GPS:', err);
        this.engine.showToast('⚠️ Không thể định vị, vui lòng chọn map có sẵn!', 'warning');
        setEnterGameLoading(false, '▶ BƯỚC VÀO NGÕ (MẶC ĐỊNH CẦU GIẤY)');
      }
    });

    // Khởi tạo hiển thị Map Gần Nhất (nếu có)
    const restoreRecentMap = () => {
      const lastMap = SearchService.getLastPlayedMap();
      if (lastMap && lobbyRecentCardContainer && lobbyRecentBadge) {
        lobbyRecentBadge.style.display = 'inline';
        lobbyRecentCardContainer.style.display = 'block';
        lobbyRecentCardContainer.innerHTML = `
          <div class="map-card" id="cardRecentMap" style="background: rgba(255, 213, 79, 0.12); border-color: #ffd54f;">
            <div class="map-card-top">
              <span class="map-card-name">🕒 ${lastMap.label.split(',')[0]} (Map Gần Nhất)</span>
              <span class="map-card-diff" style="color: #4fc3f7;">Vừa chơi</span>
            </div>
            <div class="map-card-tag">${lastMap.label}</div>
            <div class="map-card-features">
              🎯 Cảnh giác: ${lastMap.paranoia ?? 20}% • Miền: ${lastMap.region.toUpperCase()}
            </div>
          </div>
        `;

        document.getElementById('cardRecentMap')?.addEventListener('click', () => {
          document.querySelectorAll('.map-card').forEach((c) => c.classList.remove('selected'));
          document.getElementById('cardRecentMap')?.classList.add('selected');
          this.selectedMap = lastMap.mapId;
          if (lastMap.aiConditions) {
            this.currentPendingAiConditions = lastMap.aiConditions;
          }
          const recentLoc =
            SearchService.FALLBACK_LOCATIONS.find((l) => l.label === lastMap.label) ||
            DEFAULT_MAP_LOCATIONS[lastMap.mapId];
          if (recentLoc) {
            const cached = RealMapLoader.getCachedMap(recentLoc.coords);
            if (cached) {
              this.currentPendingRealMap = cached;
            }
            void RealMapLoader.loadRealMap(recentLoc.coords, lastMap.label, lastMap.aiConditions).then((m) => {
              this.currentPendingRealMap = m;
            });
          }
          this.engine.showToast(`Đã chọn lại map gần nhất: ${lastMap.label.split(',')[0]}!`, 'info');
        });
      }
    };
    restoreRecentMap();

    // Khởi tạo: Tự động nạp bản đồ thực tế của Ngõ 165 Cầu Giấy (Mặc định)
    this.selectedMap = 'cau-giay';
    const defaultInitLoc = DEFAULT_MAP_LOCATIONS['cau-giay'];
    void loadMapWithFeedback(defaultInitLoc.coords, defaultInitLoc.label, defaultInitLoc.region);

    // Xử lý tìm kiếm map trực tiếp tại Lobby
    const handleLobbySearchSubmit = async () => {
      const q = inpLobbySearch?.value?.trim();
      if (!q || !lobbySearchResultsList) return;
      lobbySearchResultsList.style.display = 'flex';
      lobbySearchResultsList.innerHTML = '<div style="font-size:11px; color:#ffd54f; padding:6px;">🔍 Đang tìm địa điểm qua OSM...</div>';

      const results = await SearchService.searchLocations(q);
      if (results.length === 0) {
        lobbySearchResultsList.innerHTML = '<div style="font-size:11px; color:#90a4ae; padding:6px;">Không tìm thấy. Hãy thử nhập tên quận/thành phố!</div>';
        return;
      }

      lobbySearchResultsList.innerHTML = results
        .slice(0, 4)
        .map(
          (r, idx) => `
        <div class="search-result-item lobby-res-item" data-idx="${idx}">
          <strong>📍 ${r.label.split(',')[0]}</strong>
          <small>${r.label}</small>
        </div>
      `
        )
        .join('');

      lobbySearchResultsList.querySelectorAll('.lobby-res-item').forEach((item) => {
        item.addEventListener('click', async () => {
          const idx = parseInt(item.getAttribute('data-idx') || '0', 10);
          const sel = results[idx];
          if (!sel) return;

          lobbySearchResultsList.style.display = 'none';
          const ok = await loadMapWithFeedback(sel.coords, sel.label, sel.region);
          if (ok) {
            const targetMapId = sel.region === 'north' ? 'cau-giay' : sel.region === 'south' ? 'ta-hien' : 'linh-dam';
            this.selectedMap = targetMapId;
            SearchService.saveLastPlayedMap({
              mapId: targetMapId,
              label: sel.label,
              region: sel.region,
              paranoia: this.currentPendingAiConditions?.initialParanoia ?? 20,
              aiConditions: this.currentPendingAiConditions || undefined,
            });
            restoreRecentMap();
            document.querySelectorAll('.map-card').forEach((c) => c.classList.remove('selected'));
            document.getElementById('cardRecentMap')?.classList.add('selected');
          }
        });
      });
    };

    btnLobbySearch?.addEventListener('click', handleLobbySearchSubmit);
    inpLobbySearch?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleLobbySearchSubmit();
    });

    // Quick chips tại Lobby
    const lobbyChips = document.querySelectorAll<HTMLElement>('.lobby-chip');
    lobbyChips.forEach((chip) => {
      chip.addEventListener('click', async () => {
        const query = chip.dataset.query;
        if (!query) return;

        if (inpLobbySearch) {
          inpLobbySearch.value = query;
        }

        const loc = findLocationByQuery(query);
        if (loc) {
          const ok = await loadMapWithFeedback(loc.coords, loc.label, loc.region);
          if (ok) {
            const targetMapId =
              loc.region === 'north'
                ? 'cau-giay'
                : loc.region === 'south'
                ? 'ta-hien'
                : 'linh-dam';
            this.selectedMap = targetMapId;

            SearchService.saveLastPlayedMap({
              mapId: targetMapId,
              label: loc.label,
              region: loc.region,
              paranoia: 20,
            });
            restoreRecentMap();

            document.querySelectorAll('.map-card').forEach((c) => c.classList.remove('selected'));
            document.getElementById('cardRecentMap')?.classList.add('selected');
          }
        }
      });
    });

    // 4. Map Selection Cards (Nạp Bản Đồ Thực Tế tương ứng)
    const mapCards = document.querySelectorAll<HTMLElement>('.map-card');
    mapCards.forEach((card) => {
      card.addEventListener('click', async () => {
        mapCards.forEach((c) => c.classList.remove('selected'));
        card.classList.add('selected');
        const m = card.dataset.map as import('../domain/types.ts').MapId;
        if (!m) return;

        this.selectedMap = m;
        const targetLoc = DEFAULT_MAP_LOCATIONS[m];
        if (targetLoc) {
          await loadMapWithFeedback(targetLoc.coords, targetLoc.label, targetLoc.region);
          SearchService.saveLastPlayedMap({
            mapId: m,
            label: targetLoc.label,
            region: targetLoc.region,
            paranoia: this.currentPendingAiConditions?.initialParanoia ?? 20,
            aiConditions: this.currentPendingAiConditions || undefined,
          });
          restoreRecentMap();
        }
      });
    });

    // 5. Enter Game Button
    if (this.btnEnterGame) {
      this.btnEnterGame.addEventListener('click', () => {
        const playerName = this.inpPlayerName?.value?.trim() || 'Dũng Corgi';

        if (!this.currentPendingRealMap && this.selectedMap) {
          const loc = DEFAULT_MAP_LOCATIONS[this.selectedMap];
          if (loc) {
            const cached = RealMapLoader.getCachedMap(loc.coords);
            if (cached) {
              this.currentPendingRealMap = cached;
            }
          }
        }

        if (this.currentPendingRealMap) {
          // Nạp trực tiếp bản đồ thực tế từ OpenStreetMap
          const realMap = this.currentPendingRealMap;
          this.camera.setMaxRadius(realMap.radiusM || 180);
          this.engine.loadRealOsmMap(
            realMap,
            this.selectedBreed,
            playerName,
            this.currentPendingAiConditions || undefined
          );
        } else {
          this.camera.setMaxRadius(100);
          this.engine.loadMap(this.selectedMap, this.selectedBreed, playerName);

          // Nếu có điều kiện sinh từ AI/Gần nhất -> áp dụng
          if (this.currentPendingAiConditions) {
            const cond = this.currentPendingAiConditions;
            this.engine.setParanoia(cond.initialParanoia);
            this.engine.setGameMasterComment(
              cond.isAiGenerated ? 'Spatial Game AI' : 'Vùng Miền Procedural',
              `[${cond.locationName}] ${cond.gameTheoryNote}`,
              8000
            );
          }
        }

        if (this.lobbyModal) {
          this.lobbyModal.style.display = 'none';
        }
        this.camera.resetZoom();
        this.pointerController?.enableFollow();
        this.engine.showToast(`Bước vào ngõ! Chạm một điểm trên đường để dắt chó!`, 'info');
      });
    }

    // Dilemma Buttons
    this.btnCooperate.addEventListener('click', () => {
      this.engine.selectDilemmaChoice('cooperate');
    });
    this.btnDefect.addEventListener('click', () => {
      this.engine.selectDilemmaChoice('defect');
    });
    this.btnBlame.addEventListener('click', () => {
      this.engine.selectDilemmaChoice('blame_shift');
    });

    // QTE Button
    this.btnQTEKick.addEventListener('click', () => {
      this.engine.executeQTEKick();
    });

    // Viral Modal actions with Web Share API
    this.btnPlayAgain.addEventListener('click', () => {
      this.viralModal.style.display = 'none';
      this.engine.resetRound();
    });
    this.btnShareThreads.addEventListener('click', async () => {
      if (navigator.share && this.viralSnapshot.src) {
        try {
          const res = await fetch(this.viralSnapshot.src);
          const blob = await res.blob();
          const file = new File([blob], 'boc-phot-cauxong.png', {
            type: 'image/png',
          });
          await navigator.share({
            title: '[Hội Cư Dân] Bắt quả tang dắt chó ị bậy!',
            text: 'Camera ngõ 165 Cầu Giấy vừa ghi lại cảnh tượng này! Đề nghị chủ chó tự giác sang dọn ngay!',
            files: [file],
          });
          return;
        } catch {
          // Fallback if sharing was cancelled or files not supported
        }
      }

      // Fallback: Tải ảnh xuống máy
      const a = document.createElement('a');
      a.href = this.viralSnapshot.src;
      a.download = 'boc-phot-cauxong.png';
      a.click();
      this.engine.showToast('Đã lưu ảnh bằng chứng bóc phốt vào máy!', 'success');
    });

    // Camera buttons
    this.btnZoomIn.addEventListener('click', () => this.camera.zoomIn());
    this.btnZoomOut.addEventListener('click', () => this.camera.zoomOut());
    this.btnCenterPlayer.addEventListener('click', () => {
      const state = this.engine.getState();
      this.camera.worldX = state.player.x;
      this.camera.worldY = state.player.y;
      this.pointerController?.enableFollow();
      this.engine.showToast('Đã căn góc nhìn về vị trí nhân vật!', 'info');
    });

    // 6. Popdown Menu Drawer Handlers (Chuẩn BaChuViaHe)
    const openPopdown = () => {
      if (this.popdownBackdrop && this.popdownMenu) {
        this.popdownBackdrop.style.display = 'block';
        this.popdownMenu.classList.add('open');
      }
    };
    const closePopdown = () => {
      if (this.popdownBackdrop && this.popdownMenu) {
        this.popdownBackdrop.style.display = 'none';
        this.popdownMenu.classList.remove('open');
      }
    };

    this.btnOpenMenu?.addEventListener('click', openPopdown);
    this.btnClosePopdown?.addEventListener('click', closePopdown);
    this.popdownBackdrop?.addEventListener('click', closePopdown);

    // Popdown: Search & AI Map Item
    this.popItemSearch?.addEventListener('click', () => {
      closePopdown();
      this.openSearchModal();
    });

    // Popdown: Open Lobby
    this.popItemLobby?.addEventListener('click', () => {
      closePopdown();
      if (this.lobbyModal) {
        this.lobbyModal.style.display = 'flex';
        this.engine.setPhase('LOBBY');
      }
    });

    // Popdown: Profile / Breed
    this.popItemProfile?.addEventListener('click', () => {
      closePopdown();
      if (this.lobbyModal) {
        this.lobbyModal.style.display = 'flex';
        this.engine.setPhase('LOBBY');
      }
      this.engine.showToast('Chọn giống cún để thay đổi tốc độ và độ chịu bẩn!', 'info');
    });

    // Popdown: Game Theory Handbook
    this.popItemHandbook?.addEventListener('click', () => {
      closePopdown();
      this.openHandbookModal();
    });

    // Popdown: Toggle Haptics
    this.popItemHaptics?.addEventListener('click', () => {
      this.isHapticsEnabled = !this.isHapticsEnabled;
      if (this.hapticsStatusText) {
        this.hapticsStatusText.textContent = `Rung & Haptics: ${this.isHapticsEnabled ? 'BẬT' : 'TẮT'}`;
      }
      if (this.isHapticsEnabled) {
        Haptics.tap();
        this.engine.showToast('Đã bật phản hồi rung (Haptics)!', 'success');
      } else {
        this.engine.showToast('Đã tắt rung!', 'info');
      }
    });

    // Popdown: Restart Round
    this.popItemRestart?.addEventListener('click', () => {
      closePopdown();
      this.engine.resetRound();
      this.camera.resetZoom();
      this.pointerController?.enableFollow();
      this.engine.showToast('Đã khởi động lại vòng dạo phố!', 'info');
    });

    // Popdown: Chơi tại Vị trí của bạn
    this.popItemCurrentLocation?.addEventListener('click', async () => {
      closePopdown();
      this.engine.showToast('📍 Đang định vị GPS / IP của bạn...', 'info');
      try {
        const loc = await LocationService.getCurrentLocation();
        this.engine.showToast(`🎯 Đã tìm thấy: ${loc.streetName}! Đang nạp bản đồ...`, 'info');
        const ok = await loadMapWithFeedback(loc.coords, loc.label, loc.region);
        if (ok && this.currentPendingRealMap) {
          const realMap = this.currentPendingRealMap;
          this.camera.setMaxRadius(realMap.radiusM || 180);
          const playerName = this.inpPlayerName?.value?.trim() || 'Dũng Corgi';
          this.engine.loadRealOsmMap(
            realMap,
            this.selectedBreed,
            playerName,
            this.currentPendingAiConditions || undefined
          );
          if (this.lobbyModal) {
            this.lobbyModal.style.display = 'none';
          }
          this.camera.resetZoom();
          this.pointerController?.enableFollow();
          this.engine.showToast(`Bước vào ngõ ${loc.streetName}! Chạm một điểm trên đường để dắt chó!`, 'info');
        }
      } catch (err) {
        console.error('[HudManager] Lỗi GPS popdown:', err);
        this.engine.showToast('⚠️ Không thể định vị, vui lòng chọn map có sẵn!', 'warning');
      }
    });

    // Popdown: Quay Promo 9:16 (30s)
    this.popItemPromo?.addEventListener('click', () => {
      closePopdown();
      if ((window as any).__promoDirector) {
        this.engine.showToast('🎬 Đang kích hoạt kịch bản mô phỏng 30s...', 'info');
        (window as any).__promoDirector.start();
      }
    });

    // Nút đóng modal Outro Promo
    this.btnClosePromoOutro?.addEventListener('click', () => {
      if (this.promoOutroModal) {
        this.promoOutroModal.style.display = 'none';
      }
      this.engine.resetRound();
    });

    // 7. Search & AI Map Modal Handlers
    this.btnCloseSearch?.addEventListener('click', () => {
      if (this.searchModal) this.searchModal.style.display = 'none';
    });

    const handleSearchSubmit = async () => {
      const q = this.inpSearchQuery?.value.trim();
      if (!q) return;
      this.searchResultsList.innerHTML = '<div style="font-size:11px; color:#ffd54f; padding:8px;">🔍 Đang tìm kiếm địa điểm OSM...</div>';
      const results = await SearchService.searchLocations(q);
      this.renderSearchResults(results);
    };

    this.btnExecuteSearch?.addEventListener('click', handleSearchSubmit);
    this.inpSearchQuery?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') handleSearchSubmit();
    });

    // Quick Region Chips trong Search Modal
    const chips = document.querySelectorAll<HTMLElement>('#searchModal .region-chip');
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const query = chip.dataset.query;
        if (!query) return;

        if (this.inpSearchQuery) {
          this.inpSearchQuery.value = query;
        }

        const loc = findLocationByQuery(query);
        if (loc) {
          const cached = RealMapLoader.getCachedMap(loc.coords);
          if (cached) {
            this.currentPendingRealMap = cached;
          }
          // Gọi ngầm loadRealMap để preload vào cache luôn
          void RealMapLoader.loadRealMap(loc.coords, loc.label).then((mapData) => {
            this.currentPendingRealMap = mapData;
          });
        }

        void handleSearchSubmit();
      });
    });

    // Nút định vị GPS trong Search Modal
    this.btnSearchMyGps?.addEventListener('click', async () => {
      this.engine.showToast('📍 Đang định vị GPS / IP của bạn...', 'info');
      try {
        const loc = await LocationService.getCurrentLocation();
        if (this.inpSearchQuery) {
          this.inpSearchQuery.value = loc.streetName || loc.label;
        }
        this.engine.showToast(`🎯 Đã định vị: ${loc.streetName || loc.label}`, 'info');
        await handleSearchSubmit();
      } catch (err) {
        console.error('[HudManager] Lỗi GPS search modal:', err);
        this.engine.showToast('⚠️ Không thể định vị, vui lòng nhập tên khu phố!', 'warning');
      }
    });

    // Confirm AI Map
    this.btnConfirmAiMap?.addEventListener('click', () => {
      if (!this.currentPendingAiConditions) return;
      const cond = this.currentPendingAiConditions;
      this.searchModal.style.display = 'none';

      if (this.currentPendingRealMap) {
        this.camera.setMaxRadius(this.currentPendingRealMap.radiusM || 180);
        this.engine.loadRealOsmMap(
          this.currentPendingRealMap,
          this.selectedBreed,
          this.engine.getState().playerName,
          cond
        );
      } else {
        this.camera.setMaxRadius(100);
        const targetMapId = cond.region === 'north' ? 'cau-giay' : cond.region === 'south' ? 'ta-hien' : 'linh-dam';
        this.engine.loadMap(targetMapId, this.selectedBreed, this.engine.getState().playerName);
        this.engine.setParanoia(cond.initialParanoia);
        this.engine.setGameMasterComment(
          cond.isAiGenerated ? 'Spatial Game AI' : 'Vùng Miền Procedural',
          `[${cond.locationName}] ${cond.gameTheoryNote}`,
          8000
        );
      }

      this.engine.showToast(`Đã nạp địa bàn: ${cond.locationName}!`, 'success');
      this.camera.resetZoom();
      this.pointerController?.enableFollow();
    });
  }

  private openSearchModal(): void {
    if (!this.searchModal) return;
    this.searchModal.style.display = 'flex';
    this.aiGenPreview.style.display = 'none';
    this.currentPendingAiConditions = null;

    // Hiển thị lịch sử tìm kiếm gần đây hoặc địa điểm hot
    const recent = SearchService.getRecentSearches();
    const listToRender = recent.length > 0 ? recent : SearchService.FALLBACK_LOCATIONS.slice(0, 6);
    this.renderSearchResults(listToRender);
  }

  private renderSearchResults(results: LocationResult[]): void {
    if (results.length === 0) {
      this.searchResultsList.innerHTML = '<div style="font-size:11px; color:#90a4ae; padding:8px;">Không tìm thấy. Hãy thử nhập tên quận/thành phố!</div>';
      return;
    }

    this.searchResultsList.innerHTML = results
      .map(
        (r, idx) => `
      <div class="search-result-item" data-idx="${idx}">
        <strong>📍 ${r.label.split(',')[0]}</strong>
        <small>${r.label} (${r.region === 'north' ? 'Miền Bắc' : r.region === 'central' ? 'Miền Trung' : 'Miền Nam'})</small>
      </div>
    `
      )
      .join('');

    this.searchResultsList.querySelectorAll('.search-result-item').forEach((item) => {
      item.addEventListener('click', async () => {
        const idx = parseInt(item.getAttribute('data-idx') || '0', 10);
        const selected = results[idx];
        if (!selected) return;

        SearchService.saveRecentSearch(selected);

        // Hiển thị loading AI preview
        this.aiGenPreview.style.display = 'flex';
        this.aiGenTitle.textContent = '🌐 ĐANG TẢI BẢN ĐỒ THỰC TẾ OSM & AI...';
        this.aiGenDesc.textContent = `Đang phân tích địa bàn "${selected.label.split(',')[0]}" và tải mạng lưới đường sá thực tế...`;
        this.btnConfirmAiMap.style.display = 'none';

        // Gọi AI Map Generator & Tải live OSM Map
        const cond = await AiMapGenerator.generateInitialConditions(selected.label, selected.region);
        this.currentPendingAiConditions = cond;

        const realMap = await RealMapLoader.loadRealMap(selected.coords, selected.label, cond);
        this.currentPendingRealMap = realMap;

        this.aiGenTitle.textContent = realMap.isRealOsm
          ? `🗺️ BẢN ĐỒ THỰC TẾ: ${cond.locationName.toUpperCase()}`
          : `✨ KHU PHỐ: ${cond.locationName.toUpperCase()}`;
        this.aiGenDesc.innerHTML = `
          <strong>Kiểu ngõ:</strong> ${cond.neighborhoodType}<br>
          <strong>Đường sá OSM:</strong> ${realMap.roads.length} tuyến đường thực tế<br>
          <strong>Cảnh giác:</strong> ${cond.initialParanoia}% • <strong>Mật độ:</strong> ${(cond.density * 100).toFixed(0)}%<br>
          <strong>Đặc trưng:</strong> ${cond.landmarks.map((l) => l.name).join(', ') || 'Nhiều góc chết'}<br>
          <em style="color:#ffd54f;">"${cond.gameTheoryNote}"</em>
        `;
        this.btnConfirmAiMap.style.display = 'block';
      });
    });
  }

  private openHandbookModal(): void {
    if (!this.handbookModal) return;
    this.handbookModal.innerHTML = GameTheoryHandbook.renderHtml();
    this.handbookModal.style.display = 'flex';

    document.getElementById('btnCloseHandbook')?.addEventListener('click', () => {
      this.handbookModal.style.display = 'none';
    });
    this.handbookModal.addEventListener('click', (e) => {
      if (e.target === this.handbookModal) {
        this.handbookModal.style.display = 'none';
      }
    });
  }

  public update(state: Readonly<GameState>): void {
    // 1. Top HUD
    this.bowelsBar.style.width = `${Math.min(100, state.dog.bowels)}%`;
    this.stressBar.style.width = `${Math.min(100, state.dog.stress)}%`;
    if (this.paranoiaBar) {
      this.paranoiaBar.style.width = `${Math.min(100, state.paranoia)}%`;
    }
    this.bagsValue.textContent = `${state.bagsCount}/2`;
    this.repValue.textContent = `${state.reputation}`;

    if (state.reputation <= 30) {
      this.repValue.style.color = '#ff5252';
    } else if (state.reputation <= 60) {
      this.repValue.style.color = '#ffa726';
    } else {
      this.repValue.style.color = '#4fc3f7';
    }

    // 2. Toast
    if (state.toastMessage) {
      this.toastBox.textContent = state.toastMessage.text;
      this.toastBox.classList.add('show');
    } else {
      this.toastBox.classList.remove('show');
    }

    // 3. Dilemma Overlay
    if (state.activeDilemma && state.activeDilemma.active) {
      this.dilemmaOverlay.style.display = 'flex';
      const sec = (state.activeDilemma.remainingTimeMs / 1000).toFixed(1);
      this.countdownBadge.textContent = `⏱️ ${sec}s`;
    } else {
      this.dilemmaOverlay.style.display = 'none';
    }

    // 4. QTE Overlay
    if (state.qteKick.active) {
      this.qteOverlay.style.display = 'flex';
      const swLeft = state.qteKick.sweetSpotStart * 100;
      const swWidth =
        (state.qteKick.sweetSpotEnd - state.qteKick.sweetSpotStart) * 100;
      this.qteSweetSpot.style.left = `${swLeft}%`;
      this.qteSweetSpot.style.width = `${swWidth}%`;
      this.qteCursor.style.left = `${state.qteKick.cursorPos * 100}%`;
    } else {
      this.qteOverlay.style.display = 'none';
    }

    // 5. Viral Caught Modal
    if (state.caughtEvent && this.viralModal.style.display !== 'flex') {
      this.viralModal.style.display = 'flex';
      this.viralReason.textContent = state.caughtEvent.reason;
      this.viralDisgraceBadge.textContent = `🚨 HỆ SỐ NHỤC NHÃ: x${state.caughtEvent.disgraceMultiplier.toFixed(
        1
      )} (${state.caughtEvent.caughtBy})`;
      // Capture live canvas snapshot
      this.viralSnapshot.src = this.renderer.getSnapshot();
    }

    // 6. GameMaster Live Commentary Bar (Đáy màn hình)
    if (this.gameMasterBar && state.gameMasterComment) {
      if (state.phase === 'PLAYING') {
        this.gameMasterBar.style.display = 'flex';
        if (this.gmTopicBadge) {
          this.gmTopicBadge.textContent = `[${state.gameMasterComment.theoryTopic.toUpperCase()}]`;
        }
        if (this.gmText && this.gmText.textContent !== state.gameMasterComment.text) {
          this.gmText.textContent = state.gameMasterComment.text;
        }
      } else {
        this.gameMasterBar.style.display = 'none';
      }
    }
  }

  public showGameMasterBar(): void {
    if (this.gameMasterBar) {
      this.gameMasterBar.style.display = 'flex';
    }
  }
}
